import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Activity, Brain, Target, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMG =
  "https://images.pexels.com/photos/11344318/pexels-photo-11344318.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=900";

const pillars = [
  { icon: Target, name: "Focus" },
  { icon: ShieldCheck, name: "Resilience" },
  { icon: Activity, name: "Confidence" },
  { icon: Brain, name: "Motivation" },
];

function formatCount(n) {
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return String(n);
}

export default function Landing() {
  const [stats, setStats] = useState({ athletes: null, sports: null });

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/assessment/stats`)
      .then(({ data }) => {
        if (alive)
          setStats({ athletes: data.athletes_assessed, sports: data.sports_count });
      })
      .catch(() => {
        if (alive) setStats({ athletes: 0, sports: 0 });
      });
    return () => {
      alive = false;
    };
  }, []);

  const statItems = [
    {
      label: "Athletes Assessed",
      value: stats.athletes == null ? "…" : formatCount(stats.athletes),
      sub:
        stats.sports == null
          ? null
          : stats.sports > 0
            ? `from ${stats.sports} sport${stats.sports === 1 ? "" : "s"}`
            : null,
    },
    { label: "Questions", value: "15" },
    { label: "Categories", value: "5" },
    { label: "Minutes", value: "~4" },
  ];

  return (
    <div className="min-h-screen bg-[#F7F7F5] text-[#111827]">
      {/* Nav */}
      <header className="max-w-7xl mx-auto px-6 lg:px-12 py-6 flex items-center justify-between">
        <div className="flex items-center" data-testid="brand-logo">
          <img
            src="https://customer-assets.emergentagent.com/job_sports-psych-assess/artifacts/c5xfaumz_Godspeed%20Logo%20Original%20-%20Maroon%20Final.png"
            alt="Godspeed Sports"
            className="w-[240px] h-[140px] rounded-md object-contain bg-black"
          />
        </div>
        <div className="hidden md:flex items-center gap-8 text-sm text-[#4B5563] font-medium">
          <span className="uppercase tracking-[0.18em] text-xs">Athlete Mental Assessment</span>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-8 pb-24 grid lg:grid-cols-12 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-7"
        >
          <span
            className="inline-block text-xs font-bold tracking-[0.22em] uppercase text-[#E25F38] mb-6"
            data-testid="hero-eyebrow"
          >
            Sports Psychology · Backed Assessment
          </span>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mb-6">
            Train the muscle <br />
            <span className="text-[#1C3F39]">between your ears.</span>
          </h1>
          <p className="text-lg text-[#4B5563] max-w-xl mb-10 leading-relaxed">
            A 15-question deep-dive into your mental performance — focus, resilience,
            confidence, motivation, and anxiety control. Get a category-wise breakdown
            and AI-personalized recommendations from a sports psychologist's lens.
          </p>

          <div className="flex items-center gap-4 mb-12">
            <Link to="/intake">
              <Button
                size="lg"
                className="bg-[#1C3F39] hover:bg-[#132A26] text-white rounded-full px-8 h-14 text-base font-semibold"
                data-testid="hero-start-btn"
              >
                Start Assessment
                <ArrowRight className="ml-2 w-5 h-5" strokeWidth={2.5} />
              </Button>
            </Link>
            <span className="text-sm text-[#4B5563]">Free · ~4 minutes</span>
          </div>

          <div className="flex items-center gap-10 pt-8 border-t border-[#E5E7EB]">
            {statItems.map((s) => (
              <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="font-display text-3xl text-[#1C3F39]">{s.value}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#4B5563] mt-1">
                  {s.label}
                </div>
                {s.sub && (
                  <div
                    className="text-[11px] text-[#4B5563] mt-1 italic"
                    data-testid={`stat-sub-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {s.sub}
                  </div>
                )}
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-[4/5] overflow-hidden rounded-2xl border border-[#E5E7EB]">
            <img
              src={HERO_IMG}
              alt="Indian children joyfully playing in a mustard flower field"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#1C3F39]/75 via-[#1C3F39]/20 to-transparent" />
            <div className="absolute top-6 left-6 right-6 text-white">
              <div className="font-display text-2xl leading-snug">
                "A healthy mind is best encased in a healthy body."
              </div>
              <div className="text-sm mt-2 opacity-90 font-semibold">
                — Sharad Kumar Khaitan
              </div>
            </div>
          </div>

          {/* Floating pillar card */}
          <div className="absolute -bottom-8 -left-6 hidden md:block bg-white border border-[#E5E7EB] rounded-xl p-5 w-64 shadow-sm">
            <div className="text-xs uppercase tracking-[0.2em] text-[#4B5563] mb-3">
              You'll be measured on
            </div>
            <div className="grid grid-cols-2 gap-3">
              {pillars.map((p) => (
                <div key={p.name} className="flex items-center gap-2 text-sm font-semibold">
                  <p.icon className="w-4 h-4 text-[#E25F38]" strokeWidth={2.5} />
                  {p.name}
                </div>
              ))}
              <div className="flex items-center gap-2 text-sm font-semibold col-span-2">
                <Brain className="w-4 h-4 text-[#E25F38]" strokeWidth={2.5} />
                Anxiety Management
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-24">
        <div className="text-xs uppercase tracking-[0.22em] text-[#4B5563] mb-4">
          How it works
        </div>
        <h2 className="font-display text-3xl md:text-4xl mb-12 max-w-2xl">
          Three steps to your mental performance profile.
        </h2>

        <div className="grid md:grid-cols-3 gap-6">
          {[
            { n: "01", t: "Tell us about you", d: "Name, email, and the sport you play." },
            { n: "02", t: "Answer 15 questions", d: "Honest multiple-choice. ~4 minutes." },
            { n: "03", t: "Get your insights", d: "Pie chart breakdown + AI recommendations." },
          ].map((s) => (
            <div
              key={s.n}
              className="bg-white border border-[#E5E7EB] rounded-xl p-8"
              data-testid={`step-${s.n}`}
            >
              <div className="font-display text-5xl text-[#E25F38] mb-6">{s.n}</div>
              <div className="font-display text-xl mb-2">{s.t}</div>
              <div className="text-[#4B5563]">{s.d}</div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <Link to="/intake">
            <Button
              size="lg"
              className="bg-[#1C3F39] hover:bg-[#132A26] text-white rounded-full px-8 h-14 text-base font-semibold"
              data-testid="bottom-start-btn"
            >
              Begin Now
              <ArrowRight className="ml-2 w-5 h-5" strokeWidth={2.5} />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-[#E5E7EB] py-8 text-center text-sm text-[#4B5563]">
        Godspeed Sports · Built for athletes who train every part of their game.
      </footer>
    </div>
  );
}
